import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "./users.css";
import Sidebar from "./Sidebar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faChalkboardTeacher,
  faUserCheck,
  faSearch,
  faEdit,
  faTrash,
  faPlus,
  faUser,
  faTimes,
  faExclamationTriangle,
  faCheckCircle,
  faSyncAlt,
  faFilter,
  faCalendarAlt,
  faEye,
  faLock,
  faUnlock,
  faUserShield,
  faDatabase,
  faShieldAlt
} from "@fortawesome/free-solid-svg-icons";

// No AsyncStorage needed – using localStorage for React.js (web)

const UsersManagement = () => {
    const [users, setUsers] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [availableTeachers, setAvailableTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCustomModal, setShowCustomModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);
    
    // Token state – loaded from localStorage (synchronous)
    const [token, setToken] = useState(null);
    const [tokenLoaded, setTokenLoaded] = useState(false);
    
    // Form states
    const [formData, setFormData] = useState({
        teacher_id: '',
        username: '',
        email: '',
        name: '',
        password: '',
        role: 'user',
        status: 'active'
    });

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    // API configuration
    const API_BASE = 'https://mhsdev.online/MHS-WEB/api';

    // Load token and cached data on mount (synchronous)
    useEffect(() => {
        const loadTokenAndCache = () => {
            try {
                // 1. Load token from localStorage
                const storedToken = localStorage.getItem('authToken');
                setToken(storedToken);
                setTokenLoaded(true);

                // 2. Load cached users data from localStorage
                const cachedData = localStorage.getItem('users_cache');
                if (cachedData) {
                    const parsed = JSON.parse(cachedData);
                    if (Date.now() - parsed.timestamp < 5 * 60 * 1000) { // 5 minutes cache
                        setUsers(parsed.users);
                        setAvailableTeachers(parsed.availableTeachers);
                        setLoading(false);
                    }
                }
            } catch (error) {
                console.error('Error loading token/cache:', error);
                toast.error('Failed to load authentication data');
            }
        };
        loadTokenAndCache();
    }, []);

    // Once token is loaded, fetch fresh data
    useEffect(() => {
        if (tokenLoaded) {
            fetchUsersAndTeachers();
        }
    }, [tokenLoaded]);

    // Fetch users and teachers – uses token from state
    const fetchUsersAndTeachers = async (forceRefresh = false) => {
        if (isRefreshing && !forceRefresh) return;
        
        try {
            setIsRefreshing(true);
            setLoading(true);

            // If token is not available, abort
            if (!token) {
                toast.error('No authentication token found. Please log in again.');
                setLoading(false);
                setIsRefreshing(false);
                return;
            }

            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            // Fetch users
            const usersResponse = await axios.get(`${API_BASE}/usersCrud.php?action=fetch-users`, { headers });
            // Fetch available teachers
            const teachersResponse = await axios.get(`${API_BASE}/usersCrud.php?action=get-teachers-not-in-users`, { headers });
            
            if (usersResponse.data && teachersResponse.data) {
                const usersData = usersResponse.data.users || [];
                const teachersData = teachersResponse.data.teachers || [];
                
                setUsers(usersData);
                setAvailableTeachers(teachersData);
                
                // Set last refresh time
                setLastRefreshTime(new Date());
                
                // Cache the data in localStorage (synchronous)
                const cacheData = {
                    users: usersData,
                    availableTeachers: teachersData,
                    timestamp: Date.now()
                };
                localStorage.setItem('users_cache', JSON.stringify(cacheData));
                
                toast.success('Data loaded successfully');
            } else {
                throw new Error('Invalid response format');
            }
            
        } catch (error) {
            console.error('Error fetching data:', error);
            
            // Try to load from localStorage cache if available
            try {
                const cachedData = localStorage.getItem('users_cache');
                if (cachedData) {
                    const parsed = JSON.parse(cachedData);
                    if (Date.now() - parsed.timestamp < 5 * 60 * 1000) { // 5 minutes cache
                        setUsers(parsed.users);
                        setAvailableTeachers(parsed.availableTeachers);
                        toast.info('Showing cached data. Some features may be outdated.');
                    }
                }
            } catch (cacheError) {
                console.error('Cache read error:', cacheError);
            }
            
            toast.error('Failed to load data: ' + (error.message || 'Network error'));
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    // Fetch all teachers (for stats) – placeholder
    const fetchAllTeachers = async () => {
        // Not implemented; kept for compatibility
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Open add modal with teacher data
    const openAddWithTeacher = (teacher) => {
        const username = teacher.email?.split('@')[0] || teacher.gmail?.split('@')[0] || teacher.name?.toLowerCase().replace(/\s+/g, '.');
        
        setFormData({
            teacher_id: teacher.id,
            username: username,
            email: teacher.email || teacher.gmail || '',
            name: teacher.name || '',
            password: '', // Will be auto-generated by backend if empty
            role: 'teacher', // Default role for teachers
            status: 'active'
        });
        setShowAddModal(true);
    };

    // Open custom user modal
    const openCustomModal = () => {
        setFormData({
            teacher_id: '',
            username: '',
            email: '',
            name: '',
            password: '',
            role: 'user',
            status: 'active'
        });
        setShowCustomModal(true);
    };

    // Open edit modal
    const openEditModal = (user) => {
        setSelectedUser(user);
        setFormData({
            teacher_id: user.teacher_id || '',
            username: user.username,
            email: user.email,
            name: user.name,
            password: '', // Leave blank to keep current password
            role: user.role,
            status: user.status
        });
        setShowEditModal(true);
    };

    // Open delete modal
    const openDeleteModal = (user) => {
        setSelectedUser(user);
        setShowDeleteModal(true);
    };

    // Add user from teacher
    const handleAddUser = async (e) => {
        e.preventDefault();
        try {
            if (!token) {
                toast.error('No authentication token. Please log in again.');
                return;
            }
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };
            const response = await axios.post(`${API_BASE}/usersCrud.php?action=create-user`, 
                formData,
                { headers }
            );
            
            if (response.data) {
                if (response.data.error) {
                    toast.error(response.data.error);
                } else {
                    toast.success(response.data.message || 'User created successfully');
                    
                    // Show generated password if provided
                    if (response.data.generated_password) {
                        toast.info(`Generated password: ${response.data.generated_password} - Save it now!`);
                    }
                    
                    setShowAddModal(false);
                    fetchUsersAndTeachers(true); // Force refresh
                }
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message;
            toast.error('Failed to add user: ' + errorMsg);
        }
    };

    // Add custom user
    const handleAddCustomUser = async (e) => {
        e.preventDefault();
        try {
            if (!token) {
                toast.error('No authentication token. Please log in again.');
                return;
            }
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };
            const response = await axios.post(`${API_BASE}/usersCrud.php?action=create-user`, 
                formData,
                { headers }
            );
            
            if (response.data) {
                if (response.data.error) {
                    toast.error(response.data.error);
                } else {
                    toast.success(response.data.message || 'User created successfully');
                    
                    // Show generated password if provided
                    if (response.data.generated_password) {
                        toast.info(`Generated password: ${response.data.generated_password} - Save it now!`);
                    }
                    
                    setShowCustomModal(false);
                    fetchUsersAndTeachers(true); // Force refresh
                }
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message;
            toast.error('Failed to add custom user: ' + errorMsg);
        }
    };

    // Update user
    const handleUpdateUser = async (e) => {
        e.preventDefault();
        try {
            if (!token) {
                toast.error('No authentication token. Please log in again.');
                return;
            }
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };
            const updateData = {
                ...formData,
                user_id: selectedUser.id
            };
            
            const response = await axios.post(`${API_BASE}/usersCrud.php?action=update-user`, 
                updateData,
                { headers }
            );
            
            if (response.data) {
                if (response.data.error) {
                    toast.error(response.data.error);
                } else {
                    toast.success(response.data.message || 'User updated successfully');
                    setShowEditModal(false);
                    fetchUsersAndTeachers(true); // Force refresh
                }
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message;
            toast.error('Failed to update user: ' + errorMsg);
        }
    };

    // Delete user
    const handleDeleteUser = async () => {
        try {
            if (!token) {
                toast.error('No authentication token. Please log in again.');
                return;
            }
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };
            const response = await axios.post(`${API_BASE}/usersCrud.php?action=delete-user`, 
                { user_id: selectedUser.id },
                { headers }
            );
            
            if (response.data) {
                if (response.data.error) {
                    toast.error(response.data.error);
                } else {
                    toast.success(response.data.message || 'User deleted successfully');
                    setShowDeleteModal(false);
                    fetchUsersAndTeachers(true); // Force refresh
                }
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message;
            toast.error('Failed to delete user: ' + errorMsg);
        }
    };

    // Toggle user status
    const toggleUserStatus = async (user) => {
        try {
            if (!token) {
                toast.error('No authentication token. Please log in again.');
                return;
            }
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };
            const newStatus = user.status === 'active' ? 'inactive' : 'active';
            const response = await axios.post(`${API_BASE}/usersCrud.php?action=change-user-status`, 
                {
                    user_id: user.id,
                    status: newStatus
                },
                { headers }
            );
            
            if (response.data) {
                if (response.data.error) {
                    toast.error(response.data.error);
                } else {
                    toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
                    fetchUsersAndTeachers(true); // Force refresh
                }
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message;
            toast.error('Failed to toggle user status: ' + errorMsg);
        }
    };

    // Filter users
    const filteredUsers = users.filter(user => {
        const matchesSearch = 
            user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.role?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesRole = roleFilter === 'all' || user.role === roleFilter;
        const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
        
        return matchesSearch && matchesRole && matchesStatus;
    });

    // Animation variants
    const modalVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: { 
            opacity: 1, 
            scale: 1,
            transition: { type: 'spring', damping: 25 }
        },
        exit: { opacity: 0, scale: 0.8 }
    };

    const backdropVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
        exit: { opacity: 0 }
    };

    // Format time ago
    const formatTimeAgo = (timestamp) => {
        if (!timestamp) return "Never";
        const now = new Date();
        const past = new Date(timestamp);
        const diffMs = now - past;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return past.toLocaleDateString();
    };

    // Get role display name
    const getRoleDisplayName = (role) => {
        const roleMap = {
            'admin': 'Administrator',
            'teacher': 'Teacher',
            'user': 'User',
            'student': 'Student'
        };
        return roleMap[role] || role;
    };

    // If token is still loading, show loading indicator
    if (!tokenLoaded) {
        return (
            <div className="users-management-wrapper">
                <Sidebar />
                <div className="users-management-loading-container">
                    <div className="users-management-loading-spinner"></div>
                    <div className="users-management-loading-text">Loading authentication...</div>
                </div>
            </div>
        );
    }

    if (loading && !isRefreshing) {
        return (
            <div className="users-management-wrapper">
                <Sidebar />
                <div className="users-management-loading-container">
                    <div className="users-management-loading-spinner"></div>
                    <div className="users-management-loading-text">Loading users data...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="users-management-wrapper">
            <Sidebar />
            
            <div className="users-management-main-content">
                <ToastContainer 
                    position="top-right" 
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="light"
                />
                
                {/* Header */}
                <div className="users-management-header">
                    <div className="users-management-header-content">
                        <div>
                            <h1 className="users-management-title" style={{color:'white'}}>
                                <FontAwesomeIcon icon={faUsers} className="users-management-title-icon" />
                                Users Management
                            </h1>
                            <p className="users-management-subtitle">
                                Manage system users and their permissions
                            </p>
                        </div>
                        
                        <div className="users-management-header-actions">
                            <button
                                onClick={() => fetchUsersAndTeachers(true)}
                                disabled={isRefreshing}
                                className={`users-management-refresh-btn ${isRefreshing ? 'users-management-refresh-btn-loading' : ''}`}
                            >
                                <FontAwesomeIcon 
                                    icon={faSyncAlt} 
                                    spin={isRefreshing}
                                    className="users-management-refresh-icon"
                                />
                                {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
                            </button>
                            {lastRefreshTime && (
                                <span className="users-management-last-refresh">
                                    Updated {formatTimeAgo(lastRefreshTime)}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="users-management-stats-grid">
                    <div className="users-management-stat-card users-management-stat-card-total">
                        <div className="users-management-stat-card-content">
                            <div>
                                <p className="users-management-stat-label">Total Users</p>
                                <h3 className="users-management-stat-value">{users.length}</h3>
                                <p className="users-management-stat-trend">
                                    <FontAwesomeIcon icon={faUsers} /> 
                                    {users.filter(u => u.status === 'active').length} active
                                </p>
                            </div>
                            <div className="users-management-stat-icon">
                                <FontAwesomeIcon icon={faUsers} />
                            </div>
                        </div>
                    </div>
                    
                    <div className="users-management-stat-card users-management-stat-card-teachers">
                        <div className="users-management-stat-card-content">
                            <div>
                                <p className="users-management-stat-label">Available Teachers</p>
                                <h3 className="users-management-stat-value">{availableTeachers.length}</h3>
                                <p className="users-management-stat-trend">
                                    <FontAwesomeIcon icon={faChalkboardTeacher} /> 
                                    Ready to add as users
                                </p>
                            </div>
                            <div className="users-management-stat-icon">
                                <FontAwesomeIcon icon={faChalkboardTeacher} />
                            </div>
                        </div>
                    </div>
                    
                    <div className="users-management-stat-card users-management-stat-card-active">
                        <div className="users-management-stat-card-content">
                            <div>
                                <p className="users-management-stat-label">Active Users</p>
                                <h3 className="users-management-stat-value">
                                    {users.filter(u => u.status === 'active').length}
                                </h3>
                                <p className="users-management-stat-trend">
                                    <FontAwesomeIcon icon={faUserCheck} /> 
                                    {Math.round((users.filter(u => u.status === 'active').length / Math.max(users.length, 1)) * 100)}% active rate
                                </p>
                            </div>
                            <div className="users-management-stat-icon">
                                <FontAwesomeIcon icon={faUserCheck} />
                            </div>
                        </div>
                    </div>
                    
                    <div className="users-management-stat-card users-management-stat-card-admin">
                        <div className="users-management-stat-card-content">
                            <div>
                                <p className="users-management-stat-label">Administrators</p>
                                <h3 className="users-management-stat-value">
                                    {users.filter(u => u.role === 'admin').length}
                                </h3>
                                <p className="users-management-stat-trend">
                                    <FontAwesomeIcon icon={faUserShield} /> 
                                    System administrators
                                </p>
                            </div>
                            <div className="users-management-stat-icon">
                                <FontAwesomeIcon icon={faUserShield} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Available Teachers Section */}
                {availableTeachers.length > 0 && (
                    <div className="users-management-available-section">
                        <div className="users-management-section-header">
                            <div className="users-management-section-title">
                                <FontAwesomeIcon icon={faChalkboardTeacher} />
                                <h2>Teachers Available for User Creation</h2>
                                <span className="users-management-section-badge">
                                    {availableTeachers.length} available
                                </span>
                            </div>
                            <div className="users-management-section-actions">
                                <button
                                    onClick={openCustomModal}
                                    className="users-management-custom-user-btn"
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    Add Custom User
                                </button>
                            </div>
                        </div>
                        
                        <div className="users-management-teachers-grid">
                            {availableTeachers.slice(0, 6).map(teacher => (
                                <div key={teacher.id} className="users-management-teacher-card">
                                    <div className="users-management-teacher-card-content">
                                        <div className="users-management-teacher-avatar">
                                            <FontAwesomeIcon icon={faUser} />
                                        </div>
                                        <div className="users-management-teacher-info">
                                            <h4 className="users-management-teacher-name">{teacher.name}</h4>
                                            <p className="users-management-teacher-email">{teacher.email || teacher.gmail}</p>
                                            <div className="users-management-teacher-meta">
                                                <span>{teacher.primary_mobile || 'No phone'}</span>
                                                <span>•</span>
                                                <span>{teacher.highest_education || 'Not specified'}</span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => openAddWithTeacher(teacher)}
                                            className="users-management-add-teacher-btn"
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                            Add as User
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Users Table Section */}
                <div className="users-management-table-container">
                    {/* Table Header */}
                    <div className="users-management-table-header">
                        <div className="users-management-table-toolbar">
                            <div className="users-management-search-container">
                                <FontAwesomeIcon icon={faSearch} className="users-management-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search users by name, email, or username..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="users-management-search-input"
                                />
                                {searchTerm && (
                                    <button 
                                        onClick={() => setSearchTerm('')}
                                        className="users-management-search-clear"
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                )}
                            </div>
                            
                            <div className="users-management-filters-container">
                                <div className="users-management-filter-group">
                                    <FontAwesomeIcon icon={faFilter} className="users-management-filter-icon" />
                                    <select
                                        value={roleFilter}
                                        onChange={(e) => setRoleFilter(e.target.value)}
                                        className="users-management-filter-select"
                                    >
                                        <option value="all">All Roles</option>
                                        <option value="admin">Admin</option>
                                        <option value="teacher">Teacher</option>
                                        <option value="student">Student</option>
                                        <option value="user">User</option>
                                    </select>
                                </div>
                                
                                <div className="users-management-filter-group">
                                    <FontAwesomeIcon icon={faUserCheck} className="users-management-filter-icon" />
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="users-management-filter-select"
                                    >
                                        <option value="all">All Status</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        
                        <div className="users-management-table-summary">
                            <span className="users-management-summary-text">
                                Showing {filteredUsers.length} of {users.length} users
                            </span>
                            {searchTerm && (
                                <span className="users-management-search-indicator">
                                    Searching for: "{searchTerm}"
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="users-management-table-wrapper">
                        <table className="users-management-table">
                            <thead>
                                <tr>
                                    <th className="users-management-table-header-cell">User</th>
                                    <th className="users-management-table-header-cell">Role</th>
                                    <th className="users-management-table-header-cell">Status</th>
                                    <th className="users-management-table-header-cell">Last Login</th>
                                    <th className="users-management-table-header-cell">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.length > 0 ? (
                                    filteredUsers.map((user, index) => (
                                        <motion.tr
                                            key={user.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className="users-management-table-row"
                                        >
                                            <td className="users-management-table-cell">
                                                <div className="users-management-user-cell">
                                                    <div className={`users-management-user-avatar ${user.role}`}>
                                                        <FontAwesomeIcon icon={faUser} />
                                                    </div>
                                                    <div className="users-management-user-details">
                                                        <h4 className="users-management-user-name">{user.name}</h4>
                                                        <p className="users-management-user-info">
                                                            <span className="users-management-user-username">{user.username}</span>
                                                            <span>•</span>
                                                            <span className="users-management-user-email">{user.email}</span>
                                                        </p>
                                                        <div className="users-management-user-id">
                                                            ID: {user.id}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="users-management-table-cell">
                                                <span className={`users-management-badge users-management-badge-role users-management-badge-${user.role}`}>
                                                    <FontAwesomeIcon icon={user.role === 'admin' ? faUserShield : user.role === 'teacher' ? faChalkboardTeacher : faUser} />
                                                    {getRoleDisplayName(user.role)}
                                                </span>
                                            </td>
                                            <td className="users-management-table-cell">
                                                <div className="users-management-status-cell">
                                                    <span className={`users-management-badge users-management-badge-status users-management-badge-${user.status}`}>
                                                        {user.status === 'active' ? (
                                                            <><FontAwesomeIcon icon={faCheckCircle} /> Active</>
                                                        ) : (
                                                            <><FontAwesomeIcon icon={faTimes} /> Inactive</>
                                                        )}
                                                    </span>
                                                    <button
                                                        onClick={() => toggleUserStatus(user)}
                                                        className="users-management-status-toggle"
                                                        title={`${user.status === 'active' ? 'Deactivate' : 'Activate'} user`}
                                                    >
                                                        <FontAwesomeIcon icon={user.status === 'active' ? faLock : faUnlock} />
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="users-management-table-cell">
                                                <div className="users-management-last-login">
                                                    {user.last_login ? (
                                                        <>
                                                            <FontAwesomeIcon icon={faCalendarAlt} />
                                                            <span>{formatTimeAgo(user.last_login)}</span>
                                                        </>
                                                    ) : (
                                                        <span className="users-management-never-login">Never logged in</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="users-management-table-cell">
                                                <div className="users-management-action-buttons">
                                                    <button
                                                        onClick={() => openEditModal(user)}
                                                        className="users-management-action-btn users-management-action-btn-edit"
                                                        title="Edit user"
                                                    >
                                                        <FontAwesomeIcon icon={faEdit} />
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => openDeleteModal(user)}
                                                        className="users-management-action-btn users-management-action-btn-delete"
                                                        title="Delete user"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="users-management-empty-state">
                                            <div className="users-management-empty-content">
                                                <FontAwesomeIcon icon={faUsers} className="users-management-empty-icon" />
                                                <h3>No users found</h3>
                                                <p>
                                                    {searchTerm 
                                                        ? `No users match your search for "${searchTerm}"`
                                                        : 'No users available. Add your first user!'}
                                                </p>
                                                <button
                                                    onClick={openCustomModal}
                                                    className="users-management-empty-action-btn"
                                                >
                                                    <FontAwesomeIcon icon={faPlus} />
                                                    Add New User
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Table Footer */}
                    <div className="users-management-table-footer">
                        <div className="users-management-pagination">
                            <span className="users-management-pagination-info">
                                Showing {filteredUsers.length} users
                            </span>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <footer className="users-management-footer">
                    <div className="users-management-footer-content">
                        <div className="users-management-footer-info">
                            <FontAwesomeIcon icon={faDatabase} className="users-management-footer-icon" />
                            <span>Total Records: {users.length} users</span>
                        </div>
                        <div className="users-management-footer-security">
                            <FontAwesomeIcon icon={faShieldAlt} />
                            <span >Secure User Management System</span>
                        </div>
                        <div className="users-management-footer-copyright">
                            © {new Date().getFullYear()} School Management System • Users Management Module v1.0
                        </div>
                    </div>
                </footer>

                {/* Add User Modal */}
                <AnimatePresence>
                    {showAddModal && (
                        <>
                            <motion.div
                                className="users-management-modal-backdrop"
                                variants={backdropVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                onClick={() => setShowAddModal(false)}
                            />
                            <div className="users-management-modal-container">
                                <motion.div
                                    className="users-management-modal"
                                    variants={modalVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="users-management-modal-header">
                                        <div className="users-management-modal-title">
                                            <FontAwesomeIcon icon={faChalkboardTeacher} />
                                            <h3>Add User from Teacher</h3>
                                        </div>
                                        <button
                                            onClick={() => setShowAddModal(false)}
                                            className="users-management-modal-close"
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </div>
                                    
                                    <form onSubmit={handleAddUser} className="users-management-form">
                                        <div className="users-management-form-content">
                                            <div className="users-management-form-group">
                                                <label className="users-management-form-label">
                                                    Teacher Name
                                                </label>
                                                <input
                                                    type="text"
                                                    value={formData.name}
                                                    disabled
                                                    className="users-management-form-input users-management-form-input-disabled"
                                                />
                                                <small className="users-management-form-help">
                                                    Teacher name is automatically populated
                                                </small>
                                            </div>
                                            
                                            <div className="users-management-form-grid">
                                                <div className="users-management-form-group">
                                                    <label className="users-management-form-label">
                                                        Username *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="username"
                                                        value={formData.username}
                                                        onChange={handleInputChange}
                                                        required
                                                        className="users-management-form-input"
                                                        placeholder="Enter username"
                                                    />
                                                </div>
                                                
                                                <div className="users-management-form-group">
                                                    <label className="users-management-form-label">
                                                        Email *
                                                    </label>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleInputChange}
                                                        required
                                                        className="users-management-form-input"
                                                        placeholder="Enter email address"
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="users-management-form-group">
                                                <label className="users-management-form-label">
                                                    Password (Optional)
                                                </label>
                                                <input
                                                    type="password"
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleInputChange}
                                                    className="users-management-form-input"
                                                    placeholder="Leave blank to auto-generate"
                                                />
                                                <small className="users-management-form-help">
                                                    Leave blank to auto-generate a secure password
                                                </small>
                                            </div>
                                            
                                            <div className="users-management-form-grid">
                                                <div className="users-management-form-group">
                                                    <label className="users-management-form-label">
                                                        Role
                                                    </label>
                                                    <select
                                                        name="role"
                                                        value={formData.role}
                                                        onChange={handleInputChange}
                                                        className="users-management-form-select"
                                                    >
                                                        <option value="teacher">Teacher</option>
                                                        <option value="admin">Admin</option>
                                                        <option value="user">User</option>
                                                    </select>
                                                </div>
                                                
                                                <div className="users-management-form-group">
                                                    <label className="users-management-form-label">
                                                        Status
                                                    </label>
                                                    <select
                                                        name="status"
                                                        value={formData.status}
                                                        onChange={handleInputChange}
                                                        className="users-management-form-select"
                                                    >
                                                        <option value="active">Active</option>
                                                        <option value="inactive">Inactive</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="users-management-modal-footer">
                                            <button
                                                type="button"
                                                onClick={() => setShowAddModal(false)}
                                                className="users-management-modal-btn users-management-modal-btn-cancel"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="users-management-modal-btn users-management-modal-btn-primary"
                                            >
                                                <FontAwesomeIcon icon={faPlus} />
                                                Add User
                                            </button>
                                        </div>
                                    </form>
                                </motion.div>
                            </div>
                        </>
                    )}
                </AnimatePresence>

                {/* Custom User Modal */}
                <AnimatePresence>
                    {showCustomModal && (
                        <>
                            <motion.div
                                className="users-management-modal-backdrop"
                                variants={backdropVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                onClick={() => setShowCustomModal(false)}
                            />
                            <div className="users-management-modal-container">
                                <motion.div
                                    className="users-management-modal"
                                    variants={modalVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="users-management-modal-header users-management-modal-header-custom">
                                        <div className="users-management-modal-title">
                                            <FontAwesomeIcon icon={faUser} />
                                            <h3>Add Custom User</h3>
                                        </div>
                                        <button
                                            onClick={() => setShowCustomModal(false)}
                                            className="users-management-modal-close"
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </div>
                                    
                                    <form onSubmit={handleAddCustomUser} className="users-management-form">
                                        <div className="users-management-form-content">
                                            <div className="users-management-form-group">
                                                <label className="users-management-form-label">
                                                    Full Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="users-management-form-input"
                                                    placeholder="Enter full name"
                                                />
                                            </div>
                                            
                                            <div className="users-management-form-grid">
                                                <div className="users-management-form-group">
                                                    <label className="users-management-form-label">
                                                        Username *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="username"
                                                        value={formData.username}
                                                        onChange={handleInputChange}
                                                        required
                                                        className="users-management-form-input"
                                                        placeholder="Choose a username"
                                                    />
                                                </div>
                                                
                                                <div className="users-management-form-group">
                                                    <label className="users-management-form-label">
                                                        Email *
                                                    </label>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleInputChange}
                                                        required
                                                        className="users-management-form-input"
                                                        placeholder="Enter email address"
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="users-management-form-group">
                                                <label className="users-management-form-label">
                                                    Password (Optional)
                                                </label>
                                                <input
                                                    type="password"
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleInputChange}
                                                    className="users-management-form-input"
                                                    placeholder="Leave blank to auto-generate"
                                                />
                                            </div>
                                            
                                            <div className="users-management-form-grid">
                                                <div className="users-management-form-group">
                                                    <label className="users-management-form-label">
                                                        Role
                                                    </label>
                                                    <select
                                                        name="role"
                                                        value={formData.role}
                                                        onChange={handleInputChange}
                                                        className="users-management-form-select"
                                                    >
                                                        <option value="user">User</option>
                                                        <option value="admin">Admin</option>
                                                        <option value="teacher">Teacher</option>
                                                    </select>
                                                </div>
                                                
                                                <div className="users-management-form-group">
                                                    <label className="users-management-form-label">
                                                        Status
                                                    </label>
                                                    <select
                                                        name="status"
                                                        value={formData.status}
                                                        onChange={handleInputChange}
                                                        className="users-management-form-select"
                                                    >
                                                        <option value="active">Active</option>
                                                        <option value="inactive">Inactive</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="users-management-modal-footer">
                                            <button
                                                type="button"
                                                onClick={() => setShowCustomModal(false)}
                                                className="users-management-modal-btn users-management-modal-btn-cancel"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="users-management-modal-btn users-management-modal-btn-custom"
                                            >
                                                <FontAwesomeIcon icon={faPlus} />
                                                Add Custom User
                                            </button>
                                        </div>
                                    </form>
                                </motion.div>
                            </div>
                        </>
                    )}
                </AnimatePresence>

                {/* Edit Modal */}
                <AnimatePresence>
                    {showEditModal && (
                        <>
                            <motion.div
                                className="users-management-modal-backdrop"
                                variants={backdropVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                onClick={() => setShowEditModal(false)}
                            />
                            <div className="users-management-modal-container">
                                <motion.div
                                    className="users-management-modal"
                                    variants={modalVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="users-management-modal-header users-management-modal-header-edit">
                                        <div className="users-management-modal-title">
                                            <FontAwesomeIcon icon={faEdit} />
                                            <h3>Edit User</h3>
                                        </div>
                                        <button
                                            onClick={() => setShowEditModal(false)}
                                            className="users-management-modal-close"
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </div>
                                    
                                    <form onSubmit={handleUpdateUser} className="users-management-form">
                                        <div className="users-management-form-content">
                                            <div className="users-management-form-group">
                                                <label className="users-management-form-label">
                                                    Username
                                                </label>
                                                <input
                                                    type="text"
                                                    name="username"
                                                    value={formData.username}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="users-management-form-input"
                                                />
                                            </div>
                                            
                                            <div className="users-management-form-group">
                                                <label className="users-management-form-label">
                                                    Email
                                                </label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="users-management-form-input"
                                                />
                                            </div>
                                            
                                            <div className="users-management-form-group">
                                                <label className="users-management-form-label">
                                                    Name
                                                </label>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="users-management-form-input"
                                                />
                                            </div>
                                            
                                            <div className="users-management-form-group">
                                                <label className="users-management-form-label">
                                                    New Password (leave blank to keep current)
                                                </label>
                                                <input
                                                    type="password"
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleInputChange}
                                                    className="users-management-form-input"
                                                    placeholder="Enter new password if changing"
                                                />
                                            </div>
                                            
                                            <div className="users-management-form-grid">
                                                <div className="users-management-form-group">
                                                    <label className="users-management-form-label">
                                                        Role
                                                    </label>
                                                    <select
                                                        name="role"
                                                        value={formData.role}
                                                        onChange={handleInputChange}
                                                        className="users-management-form-select"
                                                    >
                                                        <option value="user">User</option>
                                                        <option value="admin">Admin</option>
                                                        <option value="teacher">Teacher</option>
                                                        <option value="student">Student</option>
                                                    </select>
                                                </div>
                                                
                                                <div className="users-management-form-group">
                                                    <label className="users-management-form-label">
                                                        Status
                                                    </label>
                                                    <select
                                                        name="status"
                                                        value={formData.status}
                                                        onChange={handleInputChange}
                                                        className="users-management-form-select"
                                                    >
                                                        <option value="active">Active</option>
                                                        <option value="inactive">Inactive</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="users-management-modal-footer">
                                            <button
                                                type="button"
                                                onClick={() => setShowEditModal(false)}
                                                className="users-management-modal-btn users-management-modal-btn-cancel"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="users-management-modal-btn users-management-modal-btn-edit"
                                            >
                                                <FontAwesomeIcon icon={faCheckCircle} />
                                                Update User
                                            </button>
                                        </div>
                                    </form>
                                </motion.div>
                            </div>
                        </>
                    )}
                </AnimatePresence>

                {/* Delete Confirmation Modal */}
                <AnimatePresence>
                    {showDeleteModal && (
                        <>
                            <motion.div
                                className="users-management-modal-backdrop"
                                variants={backdropVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                onClick={() => setShowDeleteModal(false)}
                            />
                            <div className="users-management-modal-container">
                                <motion.div
                                    className="users-management-modal users-management-delete-modal"
                                    variants={modalVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="users-management-delete-icon">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                    </div>
                                    
                                    <h3 className="users-management-delete-title">Delete User</h3>
                                    
                                    <p className="users-management-delete-message">
                                        Are you sure you want to delete user <strong>{selectedUser?.name}</strong>?
                                        This action cannot be undone and will permanently remove all user data.
                                    </p>
                                    
                                    <div className="users-management-delete-details">
                                        <p>User Details:</p>
                                        <ul>
                                            <li>Username: {selectedUser?.username}</li>
                                            <li>Email: {selectedUser?.email}</li>
                                            <li>Role: {selectedUser?.role}</li>
                                            <li>Status: {selectedUser?.status}</li>
                                        </ul>
                                    </div>
                                    
                                    <div className="users-management-delete-actions">
                                        <button
                                            type="button"
                                            onClick={() => setShowDeleteModal(false)}
                                            className="users-management-delete-btn users-management-delete-btn-cancel"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleDeleteUser}
                                            className="users-management-delete-btn users-management-delete-btn-confirm"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                            Delete User
                                        </button>
                                    </div>
                                </motion.div>
                            </div>
                        </>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default UsersManagement;